"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DotMatrixPhase } from "@/components/ui/dotmatrix-core";

/**
 * Under the OS "reduce motion" preference the dot-matrix spinners keep moving,
 * 4x slower, instead of stopping. A busy indicator that goes fully static reads
 * as a hang: on a Windows RDP session the OS flips client-area animation off
 * mid-session (Chromium then reports `prefers-reduced-motion: reduce`), and a
 * spinner that was turning simply stopped dead while the connect was still
 * running (2026-09-11). The preference still tames the motion; it no longer
 * removes the only cue that something is in progress.
 */
export const REDUCED_MOTION_SPEED_FACTOR = 0.25;

/** The OS preference itself (`prefers-reduced-motion: reduce`), tracked live. */
export function useSystemPrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");

    const update = () => {
      setPrefersReducedMotion(query.matches);
    };

    update();
    query.addEventListener("change", update);

    return () => {
      query.removeEventListener("change", update);
    };
  }, []);

  return prefersReducedMotion;
}

/**
 * Whether a dot-matrix animation must be switched off entirely. Always false —
 * see `REDUCED_MOTION_SPEED_FACTOR`: the reduced-motion preference slows the
 * cycle hooks below instead of stopping them. Kept as the single policy switch
 * every dotm-* component gates on, so the decision lives in one place.
 */
export function useDotMatrixMotionDisabled(): boolean {
  return false;
}

/** Cycle speed after the reduced-motion slowdown. */
function useMotionSpeed(speed: number): number {
  const reduced = useSystemPrefersReducedMotion();
  const safeSpeed = speed > 0 ? speed : 1;
  return reduced ? safeSpeed * REDUCED_MOTION_SPEED_FACTOR : safeSpeed;
}

export interface UseCyclePhaseOptions {
  active: boolean;
  cycleMsBase: number;
  speed?: number;
}

export function useCyclePhase({ active, cycleMsBase, speed = 1 }: UseCyclePhaseOptions): number {
  const [phase, setPhase] = useState(0);
  const safeSpeed = useMotionSpeed(speed);
  const startMsRef = useRef(0);
  const runningRef = useRef(false);
  const phaseRef = useRef(0);
  const prevCycleMsRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      runningRef.current = false;
      prevCycleMsRef.current = null;
      setPhase(0);
      return;
    }

    const raw = cycleMsBase / safeSpeed;
    const cycleMs = raw > 0 && Number.isFinite(raw) ? raw : 1000;
    let rafId = 0;

    const tick = (now: number) => {
      // The phase (not the elapsed time) survives a speed change — the
      // reduced-motion preference can flip mid-run (Windows RDP does it). If
      // the cycle length changed since the last frame, rebase the start time
      // so the phase already reached carries over instead of the same
      // elapsed time being re-read against the new cycle length (which would
      // jump the phase backward or forward). Only going inactive rewinds it.
      // Mirrors `useSteppedCycle`'s rebase.
      if (!runningRef.current) {
        startMsRef.current = now;
        runningRef.current = true;
      } else if (prevCycleMsRef.current !== null && prevCycleMsRef.current !== cycleMs) {
        startMsRef.current = now - phaseRef.current * cycleMs;
      }
      prevCycleMsRef.current = cycleMs;

      const elapsed = ((now - startMsRef.current) % cycleMs + cycleMs) % cycleMs;
      const nextPhase = elapsed / cycleMs;
      phaseRef.current = nextPhase;
      setPhase(nextPhase);
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [active, cycleMsBase, safeSpeed]);

  return phase;
}

interface UseSteppedCycleOptions {
  active: boolean;
  cycleMsBase: number;
  steps: number;
  speed?: number;
  idleStep?: number;
}

type FrameListener = (now: number) => void;

const listeners = new Set<FrameListener>();
let rafId: number | null = null;

function emit(now: number) {
  listeners.forEach((listener) => {
    listener(now);
  });
}

function tick(now: number) {
  emit(now);
  if (listeners.size > 0) {
    rafId = window.requestAnimationFrame(tick);
  } else {
    rafId = null;
  }
}

function subscribeFrame(listener: FrameListener) {
  listeners.add(listener);
  if (rafId === null) {
    rafId = window.requestAnimationFrame(tick);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
}

export function useSteppedCycle({
  active,
  cycleMsBase,
  steps,
  speed = 1,
  idleStep = 0
}: UseSteppedCycleOptions): number {
  const safeSteps = Math.max(1, Math.floor(steps));
  const safeSpeed = useMotionSpeed(speed);
  const rawCycleMs = cycleMsBase / safeSpeed;
  const rawStepMs = rawCycleMs / safeSteps;
  const stepMs = rawStepMs > 0 && Number.isFinite(rawStepMs) ? rawStepMs : 1;
  const cycleMs = stepMs * safeSteps;

  const [step, setStep] = useState(() => (active ? 0 : idleStep));
  const startMsRef = useRef<number>(0);
  const activeRef = useRef(false);
  const currentStepRef = useRef(idleStep);
  const phaseRef = useRef(0);
  const prevCycleMsRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      activeRef.current = false;
      currentStepRef.current = idleStep;
      prevCycleMsRef.current = null;
      setStep(idleStep);
      return;
    }

    const updateStep = (now: number) => {
      // The phase (not the elapsed time, and not just the step) survives a
      // speed change — see `useCyclePhase`'s matching rebase for why. If the
      // cycle length changed since the last frame, rebase the start time so
      // the current step carries over instead of the same elapsed time being
      // re-read against the new (longer/shorter) cycle length.
      if (!activeRef.current) {
        startMsRef.current = now;
        activeRef.current = true;
      } else if (prevCycleMsRef.current !== null && prevCycleMsRef.current !== cycleMs) {
        startMsRef.current = now - phaseRef.current * cycleMs;
      }
      prevCycleMsRef.current = cycleMs;

      const elapsed = Math.max(0, now - startMsRef.current);
      const wrapped = elapsed % cycleMs;
      phaseRef.current = wrapped / cycleMs;
      const nextStep = Math.floor(wrapped / stepMs) % safeSteps;
      if (nextStep !== currentStepRef.current) {
        currentStepRef.current = nextStep;
        setStep(nextStep);
      }
    };

    updateStep(performance.now());
    return subscribeFrame(updateStep);
  }, [active, cycleMs, idleStep, safeSteps, stepMs]);

  return active ? step : idleStep;
}

interface UseDotMatrixPhasesOptions {
  animated?: boolean;
  hoverAnimated?: boolean;
  speed?: number;
}

interface DotMatrixPhasesResult {
  phase: DotMatrixPhase;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function useDotMatrixPhases({
  animated = false,
  hoverAnimated = false,
  speed = 1
}: UseDotMatrixPhasesOptions): DotMatrixPhasesResult {
  const safeSpeed = speed > 0 ? speed : 1;
  const autoRun = Boolean(animated && !hoverAnimated);
  const [hoverPhase, setHoverPhase] = useState<DotMatrixPhase>("idle");
  const timeouts = useRef<number[]>([]);
  const hoverGen = useRef(0);

  const clearTimers = useCallback(() => {
    for (let i = 0; i < timeouts.current.length; i += 1) {
      window.clearTimeout(timeouts.current[i]!);
    }
    timeouts.current = [];
  }, []);

  useEffect(() => {
    hoverGen.current += 1;
    clearTimers();
    return clearTimers;
  }, [autoRun, hoverAnimated, clearTimers]);

  const onMouseEnter = useCallback(() => {
    if (!hoverAnimated || autoRun) {
      return;
    }
    clearTimers();
    const gen = ++hoverGen.current;
    setHoverPhase("collapse");
    const collapseMs = Math.max(1, Math.round(300 / safeSpeed));
    const id = window.setTimeout(() => {
      if (hoverGen.current !== gen) {
        return;
      }
      setHoverPhase("hoverRipple");
    }, collapseMs);
    timeouts.current.push(id);
  }, [hoverAnimated, autoRun, safeSpeed, clearTimers]);

  const onMouseLeave = useCallback(() => {
    if (!hoverAnimated || autoRun) {
      return;
    }
    hoverGen.current += 1;
    clearTimers();
    setHoverPhase("idle");
  }, [hoverAnimated, autoRun, clearTimers]);

  const phase: DotMatrixPhase = autoRun ? "loadingRipple" : hoverAnimated ? hoverPhase : "idle";

  return useMemo(
    () => ({
      phase,
      onMouseEnter,
      onMouseLeave
    }),
    [phase, onMouseEnter, onMouseLeave]
  );
}
